import { Chess, Move, Square } from 'chess.js';
import { ChessMove, FEN, PieceColor, PieceType } from '../../types/chess';
import { sanToEnglish } from '../../utils/sanToEnglish';

export interface ResolvedMissionMoveSan {
  isValid: boolean;
  canonicalSan: string | null;
  displaySan: string | null;
  isCheck: boolean;
  isCheckmate: boolean;
  from?: string;
  to?: string;
  promotion?: PieceType;
  move?: ChessMove;
}

const stripCheckSuffix = (san: string): string => san.trim().replace(/[+#]+$/g, '');

const convertMoveFormat = (move: Move): ChessMove => ({
  from: move.from,
  to: move.to,
  promotion: move.promotion as PieceType | undefined,
  piece: move.piece as PieceType,
  color: move.color as PieceColor,
  flags: move.flags,
  san: move.san,
});

export const resolveMissionMoveSan = (fen: FEN, san: string): ResolvedMissionMoveSan => {
  try {
    const chess = new Chess(fen);
    const legalMoves = chess.moves({ verbose: true });
    const normalizedInput = stripCheckSuffix(san);
    const matchingMoves = legalMoves.filter((move) => stripCheckSuffix(move.san) === normalizedInput);

    let resolvedMove: Move | undefined;
    if (matchingMoves.length === 1) {
      [resolvedMove] = matchingMoves;
    } else if (matchingMoves.length > 1) {
      resolvedMove = matchingMoves.find((move) => move.san === san.trim());
    }

    if (!resolvedMove) {
      return {
        isValid: false,
        canonicalSan: null,
        displaySan: null,
        isCheck: false,
        isCheckmate: false,
      };
    }

    const canonicalSan = resolvedMove.san;
    const move = convertMoveFormat(resolvedMove);

    return {
      isValid: true,
      canonicalSan,
      displaySan: canonicalSan,
      isCheck: canonicalSan.includes('+'),
      isCheckmate: canonicalSan.includes('#'),
      from: resolvedMove.from,
      to: resolvedMove.to,
      promotion: resolvedMove.promotion as PieceType | undefined,
      move,
    };
  } catch (error) {
    return {
      isValid: false,
      canonicalSan: null,
      displaySan: null,
      isCheck: false,
      isCheckmate: false,
    };
  }
};

export const getCanonicalMissionMoveSan = (fen: FEN, san: string): string => {
  const resolved = resolveMissionMoveSan(fen, san);
  return resolved.canonicalSan ?? stripCheckSuffix(san);
};

export const describeMissionMoveSan = (fen: FEN, san: string): string => {
  const resolved = resolveMissionMoveSan(fen, san);
  return sanToEnglish(resolved.canonicalSan ?? stripCheckSuffix(san));
};
